import { post, patch, put, del, get } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { handleApiResponse } from './api_util';
import { refetchCaseCategories } from './categories.js';
import { refetchCaseMerchants } from './merchants.js';
import { useContext } from 'react';
import UserProfileContext from '../context/userprofilecontext.js';
import { perfLog, perfNow } from '../utils/perfLog.js';
import { useGetAndStartCaseDatabaseStatus, isCaseDbReady } from './database_status.js';
import { segmentOf, segmentLabel } from '../utils/categoryOptions';
import { deriveCardholderName, deriveCardLabel } from '../utils/cardholder.js';

async function getTransactions() {

  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions'

  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  return processResponse(response)
}


// Short-lived presigned URL for the check/deposit-slip crop folded onto a
// transaction (#6 transaction-detail thumbnail). Returns { url, doc_kind, page }
// or null when the transaction has no crop image. Fetched on demand (when the
// lightbox opens), not via SWR — the URL is short-lived by design.
export async function getCheckImageUrl(txnID) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/check_image?txnID=${encodeURIComponent(txnID)}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}


export function useGetTransactions(shouldFetch = true) {
  // Key MUST include caseID. Without it, SWR's cache is global and
  // switching between cases will display the previous case's transactions
  // until the new fetch resolves — alarming for a forensic-accounting tool
  // even though no real cross-tenant leak occurs.
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['TRANSACTIONS', caseID] : null,
    () => getTransactions()
  );
  return result;
}


// Flat mirror of the TRANSFER edge's human verdict, for the filter panel.
//
// DataFilter filters the ENRICHED rows (not the rendered list items), and its
// filter components key on a top-level primitive field — `transfer_partner`
// is nested, so it can't be filtered directly. This is the same reason
// `hasTransferPartner` exists. Non-transfer rows are false, so a "Proposed
// Transfers Only" switch narrows to exactly the pairs awaiting a verdict.
//
// Both legs of a pair share one TRANSFER edge, so both derive the same value
// and a filtered pair never renders half-orphaned.
//
// Exported because the optimistic review patches in transfer_review.js mutate
// `transfer_partner.disposition` directly and must re-derive this alongside it
// (see the sibling `hasTransferPartner` updates there).
export const deriveIsProposedTransfer = (transaction) =>
  transaction.transfer_partner?.disposition === 'PROPOSED';

// Flat "cross-ledger" flag for the filter panel (categorization segments,
// Phase 3): the row is categorized on a different ledger's chart than its
// account's ledger — personal spending inside a business account, or business
// activity through a personal account. Catches AI exceptions and human
// re-categorizations alike. Missing ledger/segment = Personal, matching the
// backend's coalesce(..., 'PERSONAL').
export const deriveIsCrossLedger = (transaction) =>
  segmentOf(transaction.subcategory) !== (transaction.bankAccount?.ledger_segment || 'PERSONAL');


export const mapTransactionToFilterableTransaction = (transaction) => {
  return {
    ...transaction,
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    amount: transaction.transaction?.amount || 0,
    merchantName: transaction.merchant?.name ,
    merchantLocation: transaction.merchantLocation?.name || 'Unknown Location',
    // Preserve the :Location (MERCHANT_LOCATION) geocode separately — the line
    // above overwrites the merchantLocation object with just its name, which
    // drops the place_id the transaction-detail map needs (e.g. a check payee
    // address that geocodes when the canonical :Merchant cannot).
    merchantLocationGeocode: transaction.merchantLocation?.geocode || null,
    associatedNames: transaction.associatedNames?.map(name => name.name) || [],
    tripLocations: transaction.tripLocations?.map(location => location.name) || [],
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    // Ledger the row is categorized on (Personal / Business / Investment chart).
    ledgerName: segmentLabel(segmentOf(transaction.subcategory)),
    flagName: transaction.flag?.name,
    notes: transaction.transaction?.notes || '',
    hasTransferPartner: transaction.transfer_partner !== null,
    isProposedTransfer: deriveIsProposedTransfer(transaction),
    isCrossLedger: deriveIsCrossLedger(transaction),
    cardholderName: deriveCardholderName(transaction.card_view),
    cardLabel: deriveCardLabel(transaction.card_view),
  };
}

async function getEnrichedTransactionsByIDList(txnIDList) {
  console.log("getEnrichedTransactionsByIDList called with txnIDList:", txnIDList);
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/enriched',
    options: {
      body: {
        txnIDList: txnIDList,
      }
    }

  }

  const restOperation = post(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  // console.log("processResponse completed with transactions:", transactions);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);
  // console.log("getEnrichedTransactionsByIDList completed with transactions:", filterableTransactions);

  return filterableTransactions
}


export function useGetEnrichedTransactionsByIDList(txnIDList, shouldFetch = true) {
  // Scope by caseID so a case switch can't reveal cached transactions from
  // the previous case (see comment on useGetTransactions).
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['ENRICHED_TRANSACTIONS_BY_ID', caseID, txnIDList] : null,
    () => getEnrichedTransactionsByIDList(txnIDList)
  );
  return result;
}

// Paginated by-ID enriched-transactions fetch.
//
// Why this exists alongside the plain useGetEnrichedTransactionsByIDList: a
// single POST returning all enriched rows for an arbitrary ID list will hit
// the API Gateway ~10MB sync ceiling somewhere around 4-6K rows (each
// enriched record carries nested merchant/location/category/flag/transfer
// objects ≈ 1-2KB). Dashboard drilldowns on poorly-categorized cases easily
// produce ID lists of that size.
//
// We slice the ID list CLIENT-SIDE — each page POSTs only its own slice of
// IDs and gets back only those rows. No backend skip/limit is used; the
// backend handler is unchanged. Pages are wrapped as {data: [...]} so the
// cache shape matches useGetEnrichedTransactions (the full-feed) — this is
// what lets the existing edit hooks (useUpdateCategoryForTransactions etc.)
// mutate either source through the same Page[] code path with no adapter.
export function useGetEnrichedTransactionsByIDListInfinite(txnIDList, shouldFetch = true, limit = 4000) {
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const idList = Array.isArray(txnIDList) ? txnIDList : [];

  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && previousPageData.data && previousPageData.data.length === 0) return null;
    if (pageIndex * limit >= idList.length) return null;
    if (!shouldFetch || !caseID) return null;
    return ['ENRICHED_TRANSACTIONS_BY_ID_INFINITE', caseID, pageIndex, limit, idList];
  };

  const fetcher = async (key) => {
    const pageIndex = key[2];
    const pageLimit = key[3];
    const ids = key[4];
    const slice = ids.slice(pageIndex * pageLimit, (pageIndex + 1) * pageLimit);
    if (slice.length === 0) return { data: [] };
    const transactions = await getEnrichedTransactionsByIDList(slice);
    return { data: transactions };
  };

  // Auto-fetch all pages on mount so the user lands on the full drilldown
  // result, not a partial "first 4000". The initialSize ensures useSWRInfinite
  // requests every page in parallel up front; persistSize keeps it from
  // collapsing back on revalidation.
  const totalPages = Math.max(1, Math.ceil(idList.length / limit));
  const options = {
    initialSize: totalPages,
    persistSize: true,
    revalidateFirstPage: false,
  };

  return useSWRInfinite(getKey, fetcher, options);
}

async function getEnrichedTransactions(pageIndex = null, limit = 4000) {
  const skip = (pageIndex??0) * limit;
  const _t0 = perfNow();

  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/enriched${pageIndex ? `?skip=${skip}&limit=${limit}` : `?limit=${limit}`}`,
  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  const _tResp = perfNow();
  const rawResult = await processResponse(response);
  const filterableTransactions = rawResult.data.map(mapTransactionToFilterableTransaction);
  rawResult.data = filterableTransactions;
  // Primitives only. `startTs` (relative ms) lets us see whether pages overlap
  // (parallel) or run back-to-back (sequential); `netMs` is network+response,
  // `totalMs` adds JSON parse + the filterable map.
  perfLog('fetch.enriched.page', {
    page: pageIndex ?? 0,
    rows: filterableTransactions.length,
    netMs: +(_tResp - _t0).toFixed(1),
    totalMs: +(perfNow() - _t0).toFixed(1),
    startTs: +_t0.toFixed(1),
  });
  return rawResult;


}

export function flattenInfiniteData(data) {
  if (!data) return [];
  return data.reduce((all, page) => {
    if (page && page.data) {
      return all.concat(page.data);
    }
    return all;
  }, []);
}

export function getOptimisticInfiniteData(data, updateFunction) {
  // data is array of pages
  // console.log("TEST1 getOptimisticInfiniteData called", data);
  if (!data) return [];
  const result = data.map((page) => {
    if (page && page.data) {
      const updatedData = updateFunction(page.data);
      return {
        ...page,
        data: updatedData
      };
    }
    return page;
  });
  // console.log("TEST1 getOptimisticInfiniteData result", result);
  return result;
}

function updateInfiniteDataFromNewList(updatedData, data){
  // for each transaction in updatedData, find it in data and replace it.
  // Build the id -> updated transaction lookup ONCE so the per-row replace is
  // O(1) instead of updatedData.find() (O(n)) per row — on a 31k dataset the
  // .find-per-row was O(n²) and allocated a fresh array per comparison.
  console.log("TEST1 updateInfiniteDataFromNewList called", {updatedData, data});
  const updatedById = new Map();
  for (const t of updatedData) {
    if (t && t.transaction && t.transaction.id != null) {
      updatedById.set(t.transaction.id, t);
    }
  }
  const result = data.map((page) => {
    if (page && page.data) {
      const newPageData = page.data.map((transaction) => {
        const updatedTransaction = updatedById.get(transaction.transaction.id);
        return updatedTransaction || transaction;
      });
      const newPageData2 = newPageData.map((transaction) => {
        if (transaction.transfer_partner) {
          const updatedTransferPartner = updatedById.get(transaction.transfer_partner.transaction.id);
          if (updatedTransferPartner) {
            return {
              ...transaction,
              transfer_partner: updatedTransferPartner
            };
          }
        }
        return transaction;
      });
      return {
        ...page,
        data: newPageData2
      };
    }
    return page;
  });
  console.log("TEST1 updateInfiniteDataFromNewList result", result);
  return result;
}

// Full-feed page size — 4000 rows/page (prod value). Sized to keep a single
// page's enriched response under the ~6MB Lambda sync-response cap (each enriched
// row is ~1-2KB nested), so a ~40k-row case is ~10 pages fetched in one parallel
// wave (~16s). Was temporarily 1000 for multi-page dev testing.
export const ENRICHED_PAGE_LIMIT = 4000;

// Max concurrent page fetches. At prod page size a ~40k-row case is ~10 pages,
// so 10 fetches the whole remainder in a single wave (~16s vs ~24s at 6). The
// per-case Neo4j boxes are amply provisioned and see low concurrency, so the
// burst is acceptable; tune down if a case DB ever shows contention.
const ENRICHED_FETCH_CONCURRENCY = 10;

// Bounded-parallel map that preserves input order. Runs at most `limit` of `fn`
// concurrently; each worker pulls the next index until the list is exhausted.
export async function mapWithConcurrency(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const idx = next++;
      out[idx] = await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return out;
}

// Piggyback + parallel full-feed fetch.
//
// Page 0 (skip 0) carries a LIVE `total` from the backend (a count-store-cheap
// join count — measured ~free on prod), so we learn the page count from data we
// were already fetching, with NO separate count round trip. We then fetch pages
// 1..N-1 in a bounded-parallel burst. useSWRInfinite can't express this (its
// parallel mode gives getKey no previousPageData, so it can't read page 0's
// total), which is why the hook below is a plain useSWR over this orchestrator.
//
// Returns Page[] ([{data:[...]}, ...]) — the SAME shape useSWRInfinite produced
// — so flattenInfiniteData and the optimistic-edit helpers
// (getOptimisticInfiniteData / updateInfiniteDataFromNewList) are unchanged.
async function getEnrichedFeedParallel(pageLimit, maxConcurrent = ENRICHED_FETCH_CONCURRENCY) {
  const page0 = await getEnrichedTransactions(0, pageLimit); // { data:[...], total?:N }
  const pages = [page0];
  const total = Number(page0?.total);
  const t0 = perfNow();

  if (Number.isFinite(total) && total > pageLimit) {
    const lastIndex = Math.ceil(total / pageLimit) - 1; // pages 1..lastIndex
    const indices = [];
    for (let i = 1; i <= lastIndex; i++) indices.push(i);
    const rest = await mapWithConcurrency(indices, maxConcurrent, (i) => getEnrichedTransactions(i, pageLimit));
    pages.push(...rest);
  }

  // Safety tail: a row inserted mid-fetch (active upload) — or a missing/low
  // `total` — leaves the last page FULL, meaning there may be more. Keep
  // fetching sequentially until a short/empty page. The live count makes this
  // the rare exception, not the common path.
  while (pages[pages.length - 1]?.data?.length === pageLimit) {
    const p = await getEnrichedTransactions(pages.length, pageLimit);
    if (!p?.data?.length) break;
    pages.push(p);
  }

  perfLog('feed.parallelFetch', {
    pages: pages.length,
    total: Number.isFinite(total) ? total : -1,
    concurrency: maxConcurrent,
    remainderMs: +(perfNow() - t0).toFixed(1),
  });
  return pages;
}

export function useGetEnrichedTransactions(shouldFetch = true, limit = ENRICHED_PAGE_LIMIT) {
  // Scope the cache key by caseID so switching cases never returns the
  // previous case's transactions from the SWR cache. Without this, the
  // Sankey/transaction views show the prior case's data for the duration
  // of the network request after a case switch.
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;

  // Single useSWR over the piggyback+parallel orchestrator (see above). The
  // cached value is Page[], so mutate/optimistic edits work exactly as they did
  // under useSWRInfinite.
  return useSWR(
    shouldFetch && caseID ? ['ENRICHED_TRANSACTIONS', caseID, limit] : null,
    ([, , lim]) => getEnrichedFeedParallel(lim),
    { revalidateOnFocus: false }
  );
}

export function EnrichedTransactionPreloader(shouldFetch = true) {
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext.userProfile;
  const isActiveCase = userProfile?.currentCase?.status === 'ACTIVE';

  // Wait for the case DB to actually be reachable before firing the real
  // feed query. On a case that hasn't been opened in a while, the Neo4j EC2
  // instance is stopped and can take up to ~90s+ to become query-ready
  // (case-db-ec2-lifecycle.md); the query lambda's own retry budget for that
  // (~115s, dbutils.py) is well past API Gateway's hard 29s integration
  // timeout. Firing the feed query immediately on case-activation — as this
  // preloader used to — raced that cold start and burned through several
  // guaranteed-504 attempts (each a real, several-minute Lambda invocation
  // that the browser had already given up on) before one finally landed
  // inside the 29s window. Piggybacking on the same DB-status poll
  // CaseDatabaseCheck uses (shared SWR key) means the preloader still kicks
  // off the lazy EC2 start immediately, it just holds the expensive query
  // until the DB is confirmed up.
  const shouldCheckDb = shouldFetch && isActiveCase;
  const dbStatus = useGetAndStartCaseDatabaseStatus(shouldCheckDb);
  const isDbReady = isCaseDbReady(dbStatus.data?.status);

  const shouldFetchEnrichedTransactions = shouldCheckDb && isDbReady;
  useGetEnrichedTransactions(shouldFetchEnrichedTransactions);
  return null;

}

// `segment` is the ledger segment (PERSONAL / BUSINESS / INVESTMENT chart) of
// the chosen subcategory; omitted → the backend uses PERSONAL.
async function updateCategoryForTransactions(txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/category',
    options: {
      body: {
        txnIDList: txnIDList,
        category: {
          type: categoryType,
          category: categoryName,
          subcategory: subcategoryName,
          rationale: rationale,
          confidence_level: confidenceLevel,
          prediction_model: predictionModel,
          ...(segment ? { segment } : {})
        }
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseCategories();

  return filterableTransactions
}



export function useUpdateCategoryForTransactions(mutateTransactions) {
  return (txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment) => {
    const resolvedSegment = segment || 'PERSONAL';
    function optimisticUpdateCategoryForTransactions(enrichedTransactions) {
      // Set lookups instead of txnIDList.includes() per row — includes is
      // O(k), so scanning all rows made a bulk edit O(n·k); on a 31k dataset
      // with a large selection that's the dominant cost. O(1) per row here.
      const txnIDSet = new Set(txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          const newEnrichedTransaction = {
            ...enrichedTransaction,
            category: {
              type: categoryType,
              name: categoryName,
              segment: resolvedSegment
            },
            subcategory: {
              name: subcategoryName,
              type: categoryType,
              segment: resolvedSegment
            },
            category_rationale: {
              confidence_level: confidenceLevel,
              matched_basis: rationale,
              prediction_model: predictionModel
            }
          }
          // The flat filter fields read the new chart, so a re-categorized row
          // moves in or out of the Ledger / Cross-ledger filters immediately.
          newEnrichedTransaction.ledgerName = segmentLabel(resolvedSegment);
          newEnrichedTransaction.isCrossLedger = deriveIsCrossLedger(newEnrichedTransaction);
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              category: {
                type: categoryType,
                name: categoryName,
                segment: resolvedSegment
              },
              subcategory: {
                name: subcategoryName,
                type: categoryType,
                segment: resolvedSegment
              },
              category_rationale: {
                confidence_level: confidenceLevel,
                matched_basis: rationale,
                prediction_model: predictionModel
              }
            }
          }
        }
        return enrichedTransaction;
      });

    }
    // const optimisticUpdate = data => {
    //   console.log("TEST1 optimisticUpdate called", data);
    //   return data
    // }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateCategoryForTransactions),
      // optimisticData: optimisticUpdate,
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateCategoryForTransactions(txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment),
      options);
  }
}

async function updateMerchantForTransactions(txnIDList, merchantData) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/merchant',
    options: {
      body: {
        txnIDList: txnIDList,
        merchant: merchantData
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

// Apply only the merchant fields present in the payload. An absent field means
// "leave unchanged", mirroring the server's partial-update semantics, so an
// untouched field is not optimistically blanked in the UI either.
const MERCHANT_PAYLOAD_KEY_TO_FIELD = {
  merchant_id: 'id',
  merchant_name: 'name',
  merchant_website: 'website',
  merchant_phone: 'phone',
  merchant_street_address: 'street_address',
  merchant_city: 'city',
  merchant_state: 'state',
  merchant_zip: 'zip'
};

// An empty merchant_name is the unlink signal, not a rename: the server drops
// the :MERCHANT and :MERCHANT_LOCATION relationships outright. Merging name:''
// onto the existing object would leave the old website/city rendering under a
// blank name until revalidation, so null the whole thing instead.
const isMerchantRemoval = (merchantData) => merchantData?.merchant_name === '';

// Cached rows are mapTransactionToFilterableTransaction output, so they carry
// derived merchant keys next to the nested objects. Clearing only the nested
// `merchant` would leave the flattened name/location/geocode driving the row
// and the detail map. These values mirror exactly what the mapper produces for
// a transaction with no merchant and no location.
const REMOVED_MERCHANT_FIELDS = {
  merchant: null,
  merchantLocation: 'Unknown Location',
  merchantLocationGeocode: null,
  merchantName: undefined
};

function applyMerchantPatch(existingMerchant, merchantData) {
  if (isMerchantRemoval(merchantData)) {
    return null;
  }

  return Object.entries(MERCHANT_PAYLOAD_KEY_TO_FIELD).reduce((merchant, [key, field]) => {
    if (merchantData?.[key] !== undefined) {
      merchant[field] = merchantData[key];
    }
    return merchant;
  }, { ...existingMerchant });
}

export function useUpdateMerchantForTransactions(mutateTransactions) {
  return (txnIDList, merchantData) => {
    function optimisticUpdateMerchantForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      console.log("optimisticUpdateMerchantForTransactions called with txnIDList:", txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          console.log(`found transaction with id=${enrichedTransaction.transaction.id}`)
          console.log("old enriched Transaction")
          console.log(enrichedTransaction)
          const newEnrichedTransaction = isMerchantRemoval(merchantData)
            ? { ...enrichedTransaction, ...REMOVED_MERCHANT_FIELDS }
            : {
              ...enrichedTransaction,
              merchant: applyMerchantPatch(enrichedTransaction.merchant, merchantData)
            }
          console.log("new enriched Transaction")
          console.log(newEnrichedTransaction)
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: isMerchantRemoval(merchantData)
              ? { ...enrichedTransaction.transfer_partner, ...REMOVED_MERCHANT_FIELDS }
              : {
                ...enrichedTransaction.transfer_partner,
                merchant: applyMerchantPatch(enrichedTransaction.transfer_partner.merchant, merchantData)
              }
          }
        }
        return enrichedTransaction;
      });

    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateMerchantForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateMerchantForTransactions(txnIDList, merchantData),
      options);

  }
}


// --- Free-form user note on a transaction (t.notes + attribution) ---
// A note is a direct property on the :Transaction node, so it round-trips as
// transaction.transaction.notes with no read-path changes. Empty string clears.

async function updateNotesForTransactions(txnIDList, notes) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/notes',
    options: {
      body: {
        txnIDList: txnIDList,
        notes: notes
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  return filterableTransactions
}

export function useUpdateNotesForTransactions(mutateTransactions) {
  return (txnIDList, notes) => {
    function optimisticUpdateNotesForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          // Optimistically show the new note text; server response fills in the
          // authoritative attribution (notesUpdatedBy / notesUpdatedAt).
          return {
            ...enrichedTransaction,
            transaction: {
              ...enrichedTransaction.transaction,
              notes: notes,
            }
          }
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              transaction: {
                ...enrichedTransaction.transfer_partner.transaction,
                notes: notes,
              }
            }
          }
        }
        return enrichedTransaction;
      });
    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateNotesForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateNotesForTransactions(txnIDList, notes),
      options);

  }
}


// --- User correction of a scanned description (OCR misread) ---
// The server keeps the scanned text in description_original and the user's text
// in description_edited (both present only while edited), and re-enriches the
// transaction. Empty description (or one equal to the scan) reverts.

async function updateDescriptionForTransaction(txnID, description) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/description',
    options: {
      body: {
        txnID: txnID,
        description: description
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  return transactions.map(mapTransactionToFilterableTransaction);
}

// Optimistic mirror of set_transaction_description's edit/revert rule. The
// "original" is always the OCR-published text, never a previous human edit.
export function applyDescriptionEdit(txn, description) {
  const original = txn.description_edited != null
    ? (txn.description_original ?? null)
    : txn.description;
  const text = (description || '').trim();
  if (!text || text === original) {
    return {
      ...txn,
      description: original,
      description_original: null,
      description_edited: null,
      description_editedBy: null,
      description_editedByUserId: null,
      description_editedAt: null,
    };
  }
  return { ...txn, description: text, description_original: original, description_edited: text };
}

export function useUpdateDescriptionForTransaction(mutateTransactions) {
  return (txnID, description) => {
    function optimisticUpdateDescription(enrichedTransactions) {
      return enrichedTransactions.map(enrichedTransaction => {
        if (enrichedTransaction.transaction.id === txnID) {
          return { ...enrichedTransaction, transaction: applyDescriptionEdit(enrichedTransaction.transaction, description) };
        }
        if (enrichedTransaction.transfer_partner?.transaction?.id === txnID) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              transaction: applyDescriptionEdit(enrichedTransaction.transfer_partner.transaction, description),
            }
          }
        }
        return enrichedTransaction;
      });
    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateDescription),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    // Returned so the caller can surface a failure (the optimistic edit rolls back).
    return mutateTransactions(updateDescriptionForTransaction(txnID, description), options);
  }
}


async function updateFlagForTransactions(txnIDList, flagData) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/flag',
    options: {
      body: {
        txnIDList: txnIDList,
        flag: {
          name: flagData.flag,
          rationale: flagData.rationale,
          confidence_level: flagData.confidenceLevel,
          prediction_model: flagData.predictionModel
        }
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

export function useUpdateFlagForTransactions(mutateTransactions) {
  return (txnIDList, flagData) => {
    function optimisticUpdateFlagForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      // console.log("optimisticUpdateFlagForTransactions called with txnIDList:", txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          // console.log(`found transaction with id=${enrichedTransaction.transaction.id}`)
          // console.log("old enriched Transaction")
          // console.log(enrichedTransaction)
          const newEnrichedTransaction = {
            ...enrichedTransaction,
            flag: {
              ...enrichedTransaction.flag,
              name: flagData.flag,
            },
            flag_basis: {
              ...enrichedTransaction.flag_basis,
              confidence_level: flagData.confidenceLevel,
              matched_basis: flagData.rationale,
              prediction_model: flagData.predictionModel

            }
          }
            // console.log("new enriched Transaction")
            // console.log(newEnrichedTransaction)
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              flag: {
                ...enrichedTransaction.transfer_partner.flag,
                name: flagData.flag,
              },
              flag_basis: {
                ...enrichedTransaction.transfer_partner.flag_basis,
                confidence_level: flagData.confidenceLevel,
                matched_basis: flagData.rationale,
                prediction_model: flagData.predictionModel

              }
            }
          }
        }
        return enrichedTransaction;
      });

    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateFlagForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateFlagForTransactions(txnIDList, flagData),
      options);

  }
}


// --- Account-ownership Phase 2: per-transaction source-of-funds tagging ---
// (:Transaction)-[:OWNED_BY]->(:Entity). owners use entity_id + a 0-1 pct. The
// untagged remainder is NOT sourced here — that's the Phase 3 source calculation.

const transactionSourceKey = (txnID) => ['TRANSACTION_SOURCE', txnID];

async function getTransactionSource(txnID) {
  const restOperation = get({
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/source?txnID=${encodeURIComponent(txnID)}`,
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// Read a transaction's explicit source tags + untagged remainder. The editor
// loads this on open; returns {found, owners:[{entity_id, display_name, kind,
// pct, value_override}], tagged_pct, untagged_pct}.
export function useGetTransactionSource(txnID, shouldFetch = true) {
  return useSWR(
    shouldFetch && txnID ? transactionSourceKey(txnID) : null,
    ([, id]) => getTransactionSource(id),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );
}

async function setTransactionSource(txnID, owners) {
  const restOperation = patch({
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/source',
    options: { body: { txnID, owners } },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// Replace a transaction's source tags. owners: [{entity_id, pct (0-1),
// value_override?, notes?}]; empty clears. Writes the returned source into the
// cache so the editor reflects the saved state without a refetch.
export function useSetTransactionSource() {
  return (txnID, owners) => mutate(
    transactionSourceKey(txnID),
    setTransactionSource(txnID, owners),
    { revalidate: false, populateCache: true, rollbackOnError: true },
  );
}